// Alle Routen aus crm/API.md als typisierte Funktionen (Fundament).
// Seiten und Komponenten rufen nur diese Funktionen auf, nie fetch direkt.
import { http } from "./client";
import type {
  Aktivitaet,
  AnrufProtokollEingabe,
  AnrufStart,
  Anruf,
  Aufgabe,
  AufgabeAenderung,
  AufgabeEingabe,
  AufgabenParameter,
  AufgabenZaehler,
  CloseSchluesselStatus,
  CustomActivityAenderung,
  CustomActivityEingabe,
  CustomActivityType,
  CustomField,
  Disposition,
  FeedParameter,
  Health,
  Kontakt,
  KontaktEingabe,
  Lead,
  LeadAenderung,
  LeadDetail,
  LeadListe,
  LeadListenParameter,
  LeadStatus,
  Liste,
  ListenParameter,
  LoginEingabe,
  Meta,
  NotizEingabe,
  Nutzer,
  NutzerKurz,
  PasswortAenderung,
  Pipeline,
  PreflightErgebnis,
  ProfilAenderung,
  QualitaetsProbe,
  SmartView,
  SmartViewKurz,
  SuchTreffer,
  SyncStatus,
  TelefonieEinstellungen,
  TelefonieKonfiguration,
  TelefonieStatus,
  TelefonieToken,
} from "./types";

export * from "./types";
export { ApiFehler, fehlerText, ABGEMELDET_EREIGNIS } from "./client";

const kodiert = encodeURIComponent;

// --- Anmeldung (fertig) -----------------------------------------------------
export const authApi = {
  login: (eingabe: LoginEingabe) => http.post<{ user: Nutzer }>("/api/auth/login", eingabe),
  logout: () => http.post<void>("/api/auth/logout"),
  me: () => http.get<{ user: Nutzer }>("/api/auth/me"),
};

// --- Betrieb (fertig) -------------------------------------------------------
export const betriebApi = {
  health: () => http.get<Health>("/api/health"),
};

// --- Meta (lead-api) --------------------------------------------------------
export const metaApi = {
  alles: () => http.get<Meta>("/api/meta"),
  leadStatuses: () => http.get<{ data: LeadStatus[] }>("/api/meta/lead-statuses"),
  pipelines: () => http.get<{ data: Pipeline[] }>("/api/meta/pipelines"),
  customFields: (object_type: "lead" | "contact" | "opportunity" | "activity") =>
    http.get<{ data: CustomField[] }>("/api/meta/custom-fields", { object_type }),
  customActivityTypes: () => http.get<{ data: CustomActivityType[] }>("/api/meta/custom-activity-types"),
  users: () => http.get<{ data: NutzerKurz[] }>("/api/meta/users"),
};

// --- Leads und Kontakte (lead-api) ------------------------------------------
export const leadsApi = {
  liste: (p: LeadListenParameter = {}, signal?: AbortSignal) =>
    http.get<LeadListe>("/api/leads", { limit: p.limit, cursor: p.cursor, sort: p.sort, status_id: p.status_id, q: p.q }, signal),
  detail: (leadId: string, signal?: AbortSignal) => http.get<LeadDetail>(`/api/leads/${kodiert(leadId)}`, undefined, signal),
  neuLaden: (leadId: string) => http.post<LeadDetail>(`/api/leads/${kodiert(leadId)}/refresh`),
  statusSetzen: (leadId: string, status_id: string) =>
    http.patch<{ lead: Lead }>(`/api/leads/${kodiert(leadId)}/status`, { status_id }),
  aendern: (leadId: string, aenderung: LeadAenderung) => http.patch<{ lead: Lead }>(`/api/leads/${kodiert(leadId)}`, aenderung),
  kontaktAnlegen: (leadId: string, eingabe: KontaktEingabe) =>
    http.post<{ contact: Kontakt }>(`/api/leads/${kodiert(leadId)}/contacts`, eingabe),
  /** Reihenfolge der Kontakte speichern (nur lokal); der erste ist der bevorzugte. */
  kontakteOrdnen: (leadId: string, ids: string[]) =>
    http.put<{ contacts: Kontakt[] }>(`/api/leads/${kodiert(leadId)}/contacts/order`, { ids }),
  kontaktAendern: (contactId: string, eingabe: KontaktEingabe) =>
    http.patch<{ contact: Kontakt }>(`/api/contacts/${kodiert(contactId)}`, eingabe),
};

// --- Aktivitäten (lead-api) -------------------------------------------------
export const aktivitaetenApi = {
  feed: (leadId: string, p: FeedParameter = {}, signal?: AbortSignal) =>
    http.get<Liste<Aktivitaet>>(
      `/api/leads/${kodiert(leadId)}/activities`,
      {
        types: p.types,
        custom_activity_type_id: p.custom_activity_type_id,
        user_id: p.user_id,
        contact_id: p.contact_id,
        limit: p.limit,
        cursor: p.cursor,
      },
      signal,
    ),
  eine: (activityId: string) => http.get<{ activity: Aktivitaet }>(`/api/activities/${kodiert(activityId)}`),
  /** Notiz oben im Verlauf anheften oder lösen (Design-Runde 2, nur im CRM gespeichert). */
  anheften: (activityId: string, an: boolean) =>
    an
      ? http.put<{ activity: Aktivitaet }>(`/api/activities/${kodiert(activityId)}/anheften`, {})
      : http.del<{ activity: Aktivitaet }>(`/api/activities/${kodiert(activityId)}/anheften`),
  notizAnlegen: (leadId: string, eingabe: NotizEingabe) =>
    http.post<{ activity: Aktivitaet }>(`/api/leads/${kodiert(leadId)}/notes`, eingabe),
  notizAendern: (activityId: string, eingabe: NotizEingabe) =>
    http.patch<{ activity: Aktivitaet }>(`/api/notes/${kodiert(activityId)}`, eingabe),
  /** Notiz und Ergebnis einer Anruf-Aktivität (CRM-Telefonie) im Verlauf ändern; die Notiz bleibt Teil des Anrufs. */
  anrufNotizAendern: (activityId: string, eingabe: { note_html?: string; disposition?: string }) =>
    http.patch<{ activity: Aktivitaet }>(`/api/calls/${kodiert(activityId)}`, eingabe),
  customAnlegen: (leadId: string, eingabe: CustomActivityEingabe) =>
    http.post<{ activity: Aktivitaet }>(`/api/leads/${kodiert(leadId)}/custom-activities`, eingabe),
  customAendern: (activityId: string, aenderung: CustomActivityAenderung) =>
    http.patch<{ activity: Aktivitaet }>(`/api/custom-activities/${kodiert(activityId)}`, aenderung),
  notizLoeschen: (activityId: string) => http.del<void>(`/api/notes/${kodiert(activityId)}`),
  customLoeschen: (activityId: string) => http.del<void>(`/api/custom-activities/${kodiert(activityId)}`),
  anrufProtokollieren: (leadId: string, eingabe: AnrufProtokollEingabe) =>
    http.post<{ activity: Aktivitaet }>(`/api/leads/${kodiert(leadId)}/calls`, eingabe),
};

// --- Aufgaben (lead-api) ----------------------------------------------------
export const aufgabenApi = {
  liste: (p: AufgabenParameter = {}, signal?: AbortSignal) =>
    http.get<Liste<Aufgabe>>(
      "/api/tasks",
      { view: p.view, assigned_to: p.assigned_to, lead_id: p.lead_id, limit: p.limit, cursor: p.cursor },
      signal,
    ),
  zaehler: () => http.get<AufgabenZaehler>("/api/tasks/counts"),
  anlegen: (eingabe: AufgabeEingabe) => http.post<{ task: Aufgabe }>("/api/tasks", eingabe),
  aendern: (taskId: string, aenderung: AufgabeAenderung) => http.patch<{ task: Aufgabe }>(`/api/tasks/${kodiert(taskId)}`, aenderung),
  erledigen: (taskId: string) => http.patch<{ task: Aufgabe }>(`/api/tasks/${kodiert(taskId)}`, { is_complete: true }),
  verschieben: (taskId: string, date: string) => http.patch<{ task: Aufgabe }>(`/api/tasks/${kodiert(taskId)}`, { date }),
};

// --- Smart Views (lead-api) -------------------------------------------------
export const smartViewsApi = {
  liste: () => http.get<{ data: SmartViewKurz[] }>("/api/smart-views"),
  eine: (viewId: string) => http.get<{ smart_view: SmartView }>(`/api/smart-views/${kodiert(viewId)}`),
  treffer: (viewId: string, p: ListenParameter = {}, signal?: AbortSignal) =>
    http.get<LeadListe>(`/api/smart-views/${kodiert(viewId)}/leads`, { limit: p.limit, cursor: p.cursor }, signal),
};

// --- Suche (lead-api) -------------------------------------------------------
export const sucheApi = {
  suchen: (q: string, limit = 10, signal?: AbortSignal) => http.get<{ data: SuchTreffer[] }>("/api/search", { q, limit }, signal),
};

// --- Einstellungen (lead-api) -----------------------------------------------
export const einstellungenApi = {
  profil: () => http.get<{ user: Nutzer }>("/api/einstellungen/profil"),
  profilAendern: (aenderung: ProfilAenderung) => http.patch<{ user: Nutzer }>("/api/einstellungen/profil", aenderung),
  closeSchluesselSetzen: (api_key: string) =>
    http.put<{ close_key: CloseSchluesselStatus }>("/api/einstellungen/close-schluessel", { api_key }),
  closeSchluesselEntfernen: () => http.del<{ close_key: CloseSchluesselStatus }>("/api/einstellungen/close-schluessel"),
  passwortSetzen: (eingabe: PasswortAenderung) => http.put<void>("/api/einstellungen/passwort", eingabe),
  nutzer: () => http.get<{ data: Nutzer[] }>("/api/einstellungen/nutzer"),
  closeSchluesselFuerNutzer: (userId: string, api_key: string) =>
    http.put<{ close_key: CloseSchluesselStatus }>(`/api/einstellungen/nutzer/${kodiert(userId)}/close-schluessel`, { api_key }),
};

// --- Telefonie (telefonie) --------------------------------------------------
export const telefonieApi = {
  status: () => http.get<TelefonieStatus>("/api/telefonie/status"),
  token: () => http.post<TelefonieToken>("/api/telefonie/token"),
  anrufStarten: (to: string, lead_id: string, contact_id?: string | null) =>
    http.post<AnrufStart>("/api/telefonie/anrufe", { to, lead_id, contact_id: contact_id ?? null }),
  anruf: (callId: string) => http.get<{ call: Anruf }>(`/api/telefonie/anrufe/${kodiert(callId)}`),
  anrufBeenden: (callId: string, eingabe: { disposition?: Disposition | null; note_html?: string | null } = {}) =>
    http.post<{ call: Anruf }>(`/api/telefonie/anrufe/${kodiert(callId)}/ende`, eingabe),
  qualitaetMelden: (callId: string, samples: QualitaetsProbe[], summary?: Record<string, unknown>) =>
    http.post<void>(`/api/telefonie/anrufe/${kodiert(callId)}/qualitaet`, { samples, summary }),
  preflight: () => http.get<{ latest: PreflightErgebnis | null }>("/api/telefonie/preflight"),
  preflightSpeichern: (ergebnis: PreflightErgebnis) =>
    http.post<{ preflight: PreflightErgebnis }>("/api/telefonie/preflight", ergebnis),
  einstellungen: () => http.get<TelefonieEinstellungen>("/api/telefonie/einstellungen"),
  einstellungenSpeichern: (e: TelefonieEinstellungen) => http.put<TelefonieEinstellungen>("/api/telefonie/einstellungen", e),
  konfiguration: () => http.get<TelefonieKonfiguration>("/api/telefonie/konfiguration"),
  konfigurationSpeichern: (k: TelefonieKonfiguration) => http.put<TelefonieKonfiguration>("/api/telefonie/konfiguration", k),
};

// --- Abgleich (import-sync, optional) ---------------------------------------
export const syncApi = {
  status: () => http.get<SyncStatus>("/api/sync/status"),
  jetzt: (bereich = "alles") => http.post<{ started: boolean }>("/api/sync/jetzt", { bereich }),
};

// --- Oberflächen-Einstellungen je Nutzer (lead-api) -------------------------
export const oberflaecheApi = {
  lesen: () => http.get<{ settings: import("./types").OberflaechenEinstellungen }>("/api/einstellungen/oberflaeche"),
  aendern: (aenderung: Partial<import("./types").OberflaechenEinstellungen>) =>
    http.patch<{ settings: import("./types").OberflaechenEinstellungen }>("/api/einstellungen/oberflaeche", aenderung),
};

// --- Glocke: Erwähnungen (Design-Runde 2) ---------------------------------------
export const benachrichtigungenApi = {
  liste: () => http.get<{ data: import("./types").Erwaehnung[]; ungelesen: number }>("/api/benachrichtigungen"),
  gelesen: (ids?: number[]) =>
    http.post<{ data: import("./types").Erwaehnung[]; ungelesen: number }>("/api/benachrichtigungen/gelesen", ids ? { ids } : {}),
};
